import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { templateAPI } from '../../services/api'
import { Image, Video, Globe, Lock, Crown, Clock, Trash2, Copy } from 'lucide-react'
import { formatOccasionDisplay, isOccasionActive } from '../../utils/occasionUtils'
import CloneTemplateDialog from '../../editor/components/CloneTemplateDialog'
import EnhancedTemplateCard from '../TemplateCard/EnhancedTemplateCard'
import MessageDialog from '../Common/MessageDialog'

const MyTemplates = ({ viewMode = 'grid' }) => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [showCloneDialog, setShowCloneDialog] = useState(false)
  const [templateToClone, setTemplateToClone] = useState(null)
  const [messageDialog, setMessageDialog] = useState({ isOpen: false, type: 'info', title: '', message: '' })

  const fetchMyTemplates = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await templateAPI.getMyTemplates()
      
      if (response.data.success) {
        setTemplates(response.data.data.templates || [])
      } else {
        setError(response.data.message || 'Failed to fetch your templates')
      }
    } catch (error) {
      console.error('Error fetching my templates:', error)
      setError('Failed to load your templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMyTemplates()
  }, [])

  const handleTemplateClick = (template) => {
    navigate(`/templates/edit/${template?.id}`)
  }

  const handleUseTemplate = (template) => {
    // Just navigate to editor without changing stage
    navigate(`/editor/${template?.id}`)
  }

  const handleSubmitForReview = async (template) => {
    try {
      setActionLoading(prev => ({ ...prev, [template?.id]: 'submitting' }))

      await templateAPI.updateStage(template?.id, {
        stage: 'submitted',
        message: 'Submitted for review'
      })

      // Update local state
      setTemplates(prev => prev.map(t =>
        t.id === template?.id
          ? { ...t, stage: 'submitted' }
          : t
      ))

      setMessageDialog({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Template submitted for review!'
      })
    } catch (error) {
      console.error('Error updating template stage:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update template stage'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [template?.id]: null }))
    }
  }

  const handleDeleteTemplate = async (template_id) => {
    try {
      setActionLoading(prev => ({ ...prev, [template_id]: 'deleting' }))

      const response = await templateAPI.delete(template_id)

      // Remove from local state
      setTemplates(prev => prev.filter(t => t.id !== template_id))
      setDeleteConfirm(null)

      const successMessage = response?.data?.message || 'Template deleted successfully!'
      setMessageDialog({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: successMessage
      })
    } catch (error) {
      console.error('Error deleting template:', error)
      const errorMessage = error.response?.data?.message || 'Failed to delete template'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [template_id]: null }))
    }
  }

  const handleCloneTemplate = (template) => {
    setTemplateToClone(template)
    setShowCloneDialog(true)
  }

  const handleConfirmClone = async (newName) => {
    if (!templateToClone) return

    try {
      setActionLoading(prev => ({ ...prev, [templateToClone.id]: 'cloning' }))

      const response = await templateAPI.clone(templateToClone.id, { name: newName })

      if (response.data.success) {
        // Add cloned template to the beginning of the list
        setTemplates(prev => [response.data.data, ...prev])
        setShowCloneDialog(false)
        setTemplateToClone(null)
        const successMessage = response.data.message || 'Template cloned successfully!'
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: successMessage
        })
      } else {
        const errorMessage = response.data.message || 'Failed to clone template'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: errorMessage
        })
      }
    } catch (error) {
      console.error('Error cloning template:', error)
      const errorMessage = error.response?.data?.message || 'Failed to clone template'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [templateToClone.id]: null }))
    }
  }

  const handleCancelClone = () => {
    setShowCloneDialog(false)
    setTemplateToClone(null)
  }

  // Template card handlers
  const handleTemplateEdit = (template) => {
    navigate(`/templates/edit/${template?.id}`)
  }

  const handleTemplateDelete = (template) => {
    setDeleteConfirm(template?.id)
  }

  const handleTemplateUse = (template) => {
    navigate(`/editor/${template?.id}`)
  }

  const handleGenerateSample = async (template) => {
    try {
      setActionLoading(prev => ({ ...prev, [template?.id]: 'generating' }))

      const response = await templateAPI.generateSample(template?.id)

      if (response.data.success) {
        // Queue-based generation - show processing message
        const successMessage = response.data.message || 'Sample generation started! Processing in background...'
        setMessageDialog({
          isOpen: true,
          type: 'success',
          title: 'Sample Generation Started',
          message: successMessage
        })
        
        // Refresh templates after a delay to show updated status
        setTimeout(() => {
          fetchMyTemplates()
        }, 2000)
      } else {
        const errorMessage = response.data.message || 'Failed to start sample generation'
        setMessageDialog({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: errorMessage
        })
      }
    } catch (error) {
      console.error('Error generating sample:', error)
      const errorMessage = error.response?.data?.message || 'Failed to generate sample'
      setMessageDialog({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: errorMessage
      })
    } finally {
      setActionLoading(prev => ({ ...prev, [template?.id]: null }))
    }
  }

  const getTemplateIcon = (type) => {
    return type === 'video' ? <Video className="w-4 h-4" /> : <Image className="w-4 h-4" />
  }

  const getStageColor = (stage) => {
    switch (stage) {
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      case 'submitted': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={fetchMyTemplates}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">My Templates</h2>
            <p className="text-sm text-gray-600 mt-1">
              Your recent designs ({templates.length} templates)
            </p>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            Last 50 created
          </div>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-gray-400 mb-4">
            <Image className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-600 mb-4">Start creating your first template to see it here.</p>
          <button
            onClick={() => navigate('/templates/create')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Template
          </button>
        </div>
      ) : (
        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {templates.map((template) => (
                <EnhancedTemplateCard
                  key={template?.id}
                  template={{
                    ...template,
                    title: template?.name || template?.title,
                    thumbnail: template?.thumbnailUrl || template?.thumbnail,
                    category: String(template?.Category?.name || template?.category || 'General'),
                    isPro: template?.isPremium || template?.isPro || false
                  }}
                  showActions={true}
                  onEdit={handleTemplateEdit}
                  onDelete={handleTemplateDelete}
                  onClone={handleCloneTemplate}
                  onUse={handleTemplateUse}
                  actionLoading={actionLoading}
                  showBulkActions={false}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table Headers */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium text-gray-500 border-b border-gray-200">
                <div className="col-span-4">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Stage</div>
                <div className="col-span-1"></div>
              </div>
              
              {/* Template Rows */}
              {templates.map((template) => (
                <div
                  key={template?.id}
                  className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                  onClick={() => handleTemplateEdit(template)}
                >
                  <div className="col-span-4 flex items-center space-x-3">
                    <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                      {template?.thumbnailUrl ? (
                        <img
                          src={template?.thumbnailUrl}
                          alt={template?.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-gray-900 truncate">{template?.name}</span>
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-600">
                    {template?.type === 'video' ? 'Video' : 'Image'}
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-600">
                    {template?.is_public !== false ? 'Public' : 'Private'}
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-600">
                    {String(template?.Category?.name || template?.category || 'General')}
                  </div>
                  <div className="col-span-1 flex items-center text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      template?.stage === 'approved' ? 'bg-green-100 text-green-800' :
                      template?.stage === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                      template?.stage === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {template?.stage || 'pending'}
                    </span>
                  </div>
                  <div className="col-span-1 flex items-center justify-end">
                    <button className="p-1 hover:bg-gray-200 rounded">
                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Template
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteTemplate(deleteConfirm)}
                disabled={actionLoading[deleteConfirm] === 'deleting'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading[deleteConfirm] === 'deleting' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Dialog */}
      <CloneTemplateDialog
        isOpen={showCloneDialog}
        onClose={handleCancelClone}
        onConfirm={handleConfirmClone}
        originalTemplateName={templateToClone?.name || ''}
        isCloning={templateToClone ? actionLoading[templateToClone.id] === 'cloning' : false}
      />

      {/* Message Dialog */}
      <MessageDialog
        isOpen={messageDialog.isOpen}
        type={messageDialog.type}
        title={messageDialog.title}
        message={messageDialog.message}
        onClose={() => setMessageDialog({ isOpen: false, type: 'info', title: '', message: '' })}
      />
    </div>
  )
}

export default MyTemplates

